import { create } from 'zustand';
import { KnowledgeFile, Tag, SearchResult, SearchType, PermissionLevel } from '../types';
import { useCategoryStore } from './category-store';
import { supabase } from '../config/supabase';
import { uploadToR2, deleteFromR2, getPublicUrl } from '../config/storage';
import { generatePublicAccessToken } from '../utils/permissions';
import { getFileTypeFromExtension } from '../utils/file-types';
import { generatePdfThumbnailFromFile, generatePdfThumbnailFromUrl } from '../utils/pdf-thumbnail';

interface FileState {
  files: KnowledgeFile[];
  filteredFiles: KnowledgeFile[];
  selectedFile: KnowledgeFile | null;
  searchQuery: string;
  fetchFiles: () => Promise<void>;
  fetchFavorites: () => Promise<void>;
  fetchRecent: () => Promise<void>;
  searchType: SearchType;
  isLoading: boolean;
  error: string | null;
  selectedTags: Tag[];
  favoriteFiles: KnowledgeFile[];
  clearFiles: () => void;
  lastFetch: number;
  initialized: boolean;
  loading: boolean;
  thumbnailUpdatedFiles: Set<string>;
  recentFiles?: KnowledgeFile[];
  
  // 分页加载相关状态
  categoryFiles: Map<string, KnowledgeFile[]>;  // 按分类ID缓存文件
  categoryFilesLoading: Map<string, boolean>;   // 加载状态
  categoryFilesHasMore: Map<string, boolean>;   // 是否还有更多
  categoryFilesTotalCount: Map<string, number>; // 文件总数
  categoryFilesTotalSize: Map<string, number>;  // 文件总大小（字节）

  // Actions
  setFiles: (files: KnowledgeFile[]) => void;
  addFile: (file: KnowledgeFile) => void;
  updateFile: (id: string, updates: Partial<KnowledgeFile>) => void;
  deleteFile: (id: string) => void;
  selectFile: (file: KnowledgeFile | null) => void;
  setSearchQuery: (query: string) => void;
  setSearchType: (type: SearchType) => void;
  search: () => SearchResult;
  toggleTagSelection: (tag: Tag) => void;
  resetSearch: () => void;
  starFile: (id: string, starred: boolean) => void;
  filterFilesByType: (type: string) => void;
  sortFiles: (criteria: 'name' | 'date' | 'size', direction: 'asc' | 'desc') => void;
  uploadFiles: (
    files: File[],
    tags: Tag[],
    categoryId?: string,
    onProgress?: (processed: number, total: number) => void,
    options?: {
      permissionLevel?: PermissionLevel;
      isPublic?: boolean;
      ownerOnly?: boolean;
      publicExpiresAt?: string; // YYYY-MM-DD
    }
  ) => Promise<void>;
  processMissingThumbnails: () => Promise<{ processed: number; failed?: number; total?: number }>;
  // Batch operations
  deleteFiles: (ids: string[], onProgress?: (processed: number, total: number) => void) => Promise<{ deleted: number; failed: number; total: number }>;
  generateThumbnailsFor: (ids: string[], onProgress?: (processed: number, total: number) => void) => Promise<{ processed: number; failed: number; total: number }>;
  moveFilesToCategory: (ids: string[], targetCategoryId: string, onProgress?: (processed: number, total: number) => void) => Promise<{ moved: number; failed: number; total: number }>;
  updateFileThumbnail: (id: string, thumbnailKey: string) => Promise<void>;
  clearThumbnailUpdated: (id: string) => void;
  uploadFromUrl: (
    fileUrl: string,
    fileName: string,
    tags: Tag[],
    categoryId?: string,
    options?: {
      permissionLevel?: PermissionLevel;
      isPublic?: boolean;
      ownerOnly?: boolean;
      publicExpiresAt?: string; // YYYY-MM-DD
    }
  ) => Promise<void>;
  
  // 🆕 分页加载文件夹内的文件
  fetchCategoryFiles: (
    categoryId: string, 
    options?: { 
      reset?: boolean;  // 是否重置（首次加载或刷新）
      pageSize?: number; // 每页数量，默认 50
      forceRefresh?: boolean; // 强制刷新，忽略缓存
    }
  ) => Promise<{ files: KnowledgeFile[]; hasMore: boolean; totalCount: number }>;
  
  // 清除分类文件缓存
  clearCategoryFilesCache: (categoryId?: string) => void;
  
  // 🆕 服务端搜索：在特定分类下搜索文件（支持递归子分类）
  searchInCategory: (
    query: string,
    options?: {
      categoryId?: string;  // 可选：限定在某分类下搜索（包含子分类）
      limit?: number;       // 返回数量限制，默认50
    }
  ) => Promise<{ files: KnowledgeFile[]; totalCount: number }>;
}

export const useFileStore = create<FileState>((set, get) => ({
  files: [],
  filteredFiles: [],
  selectedFile: null,
  lastFetch: 0,
  initialized: false,
  searchQuery: '',
  searchType: 'basic',
  isLoading: false,
  error: null,
  selectedTags: [],
  favoriteFiles: [],
  loading: false,
  thumbnailUpdatedFiles: new Set<string>(),
  
  // 分页加载相关状态初始值
  categoryFiles: new Map<string, KnowledgeFile[]>(),
  categoryFilesLoading: new Map<string, boolean>(),
  categoryFilesHasMore: new Map<string, boolean>(),
  categoryFilesTotalCount: new Map<string, number>(),
  categoryFilesTotalSize: new Map<string, number>(),

  fetchFiles: async () => {
    try {
      const now = Date.now();
      const state = get();

      // Check if we have a valid session first
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        console.log('No active session, skipping file fetch');
        return;
      }

      // Check cache validity
      if (state.initialized && now - state.lastFetch < 300000) {
        return;
      }

      set({ isLoading: true });

      // 获取用户角色信息
      const { data: userProfile } = await supabase
        .from('v_fs_users')
        .select('role, can_access_all_tenants')
        .eq('id', session.user.id)
        .single();

      // 判断是否是超级管理员（可以看到所有文件）
      const isSuperAdminUser = userProfile?.role === 'super_admin' || 
        (userProfile?.role === 'admin' && userProfile?.can_access_all_tenants === true);

      // 构建查询
      let query = supabase
        .from('files')
        .select(`
          *,
          tags:file_tags(
            tag:tags(*)
          ),
          versions:file_versions(*)
        `)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      // 【全部文件】页面只显示自己创建的文件
      // 共享文件在具体分类/文件夹中查看
      query = query.eq('created_by', session.user.id);

      const { data: files, error } = await query;

      if (error) throw error;

      // Transform the data structure to match our KnowledgeFile type
      const transformedFiles = files.map(file => ({
        ...file,
        tags: file.tags.map((t: any) => t.tag),
        versions: file.versions || [],
        createdAt: file.created_at,
        updatedAt: file.updated_at,
        createdBy: file.created_by,
        updatedBy: file.updated_by
      })) as KnowledgeFile[];

      set({
        files: transformedFiles,
        filteredFiles: transformedFiles,
        favoriteFiles: transformedFiles.filter(f => f.starred),
        lastFetch: now,
        initialized: true
      });
    } catch (error) {
      console.error('Error fetching files:', error);
      set({ error: (error as Error).message });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchFavorites: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      // 获取用户角色信息
      const { data: userProfile } = await supabase
        .from('v_fs_users')
        .select('role, can_access_all_tenants')
        .eq('id', session.user.id)
        .single();

      // 判断是否是超级管理员
      const isSuperAdminUser = userProfile?.role === 'super_admin' || 
        (userProfile?.role === 'admin' && userProfile?.can_access_all_tenants === true);

      let query = supabase
        .from('files')
        .select('*')
        .eq('starred', true)
        .is('deleted_at', null)
        .order('updated_at', { ascending: false });

      // 【已收藏】只显示自己创建的文件
      query = query.eq('created_by', session.user.id);

      const { data: favorites, error } = await query;

      if (error) throw error;

      const transformedFavorites = favorites.map((file: any) => ({
        ...file,
        createdAt: file.created_at,
        updatedAt: file.updated_at,
        createdBy: file.created_by,
        updatedBy: file.updated_by
      })) as KnowledgeFile[];

      set({ favoriteFiles: transformedFavorites });
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  },

  fetchRecent: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      // 获取用户角色信息
      const { data: userProfile } = await supabase
        .from('v_fs_users')
        .select('role, can_access_all_tenants')
        .eq('id', session.user.id)
        .single();

      // 判断是否是超级管理员
      const isSuperAdminUser = userProfile?.role === 'super_admin' || 
        (userProfile?.role === 'admin' && userProfile?.can_access_all_tenants === true);

      let query = supabase
        .from('files')
        .select('*')
        .is('deleted_at', null)
        .order('updated_at', { ascending: false })
        .limit(10);

      // 【最近】只显示自己创建的文件
      query = query.eq('created_by', session.user.id);

      const { data: recent, error } = await query;

      if (error) throw error;

      const transformedRecent = recent.map((file: any) => ({
        ...file,
        createdAt: file.created_at,
        updatedAt: file.updated_at,
        createdBy: file.created_by,
        updatedBy: file.updated_by
      })) as KnowledgeFile[];

      set({ recentFiles: transformedRecent });
    } catch (error) {
      console.error('Error fetching recent files:', error);
    }
  },

  setFiles: (files) => set({ files, filteredFiles: files }),

  addFile: (file) => set((state) => ({
    files: [...state.files, file],
    filteredFiles: [...state.files, file]
  })),

  updateFile: async (id, updates) => {
    try {
      const { error } = await supabase
        .from('files')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      set((state) => {
        const now = new Date().toISOString();
        const updatedFiles = state.files.map(file =>
          file.id === id ? { ...file, ...updates, updatedAt: now } : file
        );

        return {
          files: updatedFiles,
          filteredFiles: updatedFiles,
          selectedFile: state.selectedFile?.id === id
            ? { ...state.selectedFile, ...updates }
            : state.selectedFile
        };
      });
    } catch (error) {
      console.error('Error updating file:', error);
      throw error;
    }
  },

  deleteFile: async (id) => {
    try {
      const file = get().files.find(f => f.id === id);
      if (!file?.storage_key) throw new Error('File not found');

      // Delete from R2 first
      try {
        await deleteFromR2(file.storage_key);
      } catch (error) {
        console.error('Error deleting from R2:', error);
        throw new Error('Failed to delete file from storage');
      }

      // Then delete from Supabase
      const { error } = await supabase
        .from('files')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        files: state.files.filter(file => file.id !== id),
        filteredFiles: state.filteredFiles.filter(file => file.id !== id),
        selectedFile: state.selectedFile?.id === id ? null : state.selectedFile
      }));

      return true;
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  },

  selectFile: (file) => set({ selectedFile: file }),

  setSearchQuery: (query) => set({ searchQuery: query }),

  setSearchType: (type) => set({ searchType: type }),

  search: () => {
    const { files, searchQuery, searchType, selectedTags } = get();
    let results: KnowledgeFile[] = [];
    const query = searchQuery.toLowerCase().trim();

    if (!query && selectedTags.length === 0) {
      results = files;
    } else {
      switch (searchType) {
        case 'basic':
          results = files.filter(file =>
            file.name.toLowerCase().includes(query) ||
            file.description?.toLowerCase().includes(query)
          );
          break;

        case 'tag':
          if (selectedTags.length > 0) {
            results = files.filter(file =>
              selectedTags.every(selectedTag =>
                file.tags.some(tag => tag.id === selectedTag.id)
              )
            );
          } else {
            results = files.filter(file =>
              file.tags.some(tag => tag.name.toLowerCase().includes(query))
            );
          }
          break;

        case 'type':
          results = files.filter(file => {
            if (!query) return true;
            return file.type.toLowerCase() === query;
          });
          break;

        case 'combined':
          results = files.filter(file => {
            const nameMatch = file.name.toLowerCase().includes(query);
            const descMatch = file.description?.toLowerCase().includes(query) || false;
            const typeMatch = file.type.toLowerCase().includes(query);
            const tagMatch = file.tags.some(tag => tag.name.toLowerCase().includes(query));

            return nameMatch || descMatch || typeMatch || tagMatch;
          });
          break;
      }
    }

    set({ filteredFiles: results });
    return { files: results, totalCount: results.length };
  },

  toggleTagSelection: (tag) => set((state) => {
    const isSelected = state.selectedTags.some(t => t.id === tag.id);
    const updatedTags = isSelected
      ? state.selectedTags.filter(t => t.id !== tag.id)
      : [...state.selectedTags, tag];

    return { selectedTags: updatedTags };
  }),

  resetSearch: () => set({
    searchQuery: '',
    selectedTags: [],
    filteredFiles: get().files,
    searchType: 'basic'
  }),

  starFile: async (id, starred) => {
    try {
      const { error } = await supabase
        .from('files')
        .update({ starred, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      // Update local state immediately
      set(state => {
        const now = new Date().toISOString();
        return {
          files: state.files.map(file =>
            file.id === id ? { ...file, starred, updatedAt: now } : file
          ),
          filteredFiles: state.filteredFiles.map(file =>
            file.id === id ? { ...file, starred, updatedAt: now } : file
          ),
          favoriteFiles: starred
            ? [...state.favoriteFiles, { ...state.files.find(f => f.id === id)!, starred, updatedAt: now }].filter(Boolean)
            : state.favoriteFiles.filter(f => f.id !== id)
        };
      });

      return true;
    } catch (error) {
      console.error('Error starring file:', error);
      throw error;
    }
  },

  filterFilesByType: (type) => set((state) => {
    if (type === 'all') {
      return { filteredFiles: state.files };
    }

    return {
      filteredFiles: state.files.filter(file => file.type === type)
    };
  }),

  sortFiles: (criteria, direction) => set((state) => {
    const sortedFiles = [...state.filteredFiles].sort((a, b) => {
      if (criteria === 'name') {
        return direction === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }

      if (criteria === 'date') {
        return direction === 'asc'
          ? new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
          : new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }

      if (criteria === 'size') {
        return direction === 'asc'
          ? a.size - b.size
          : b.size - a.size;
      }

      return 0;
    });

    return { filteredFiles: sortedFiles };
  }),

  uploadFiles: async (files: File[], tags: Tag[], categoryId?: string, onProgress?: (processed: number, total: number) => void, options?: { permissionLevel?: PermissionLevel; isPublic?: boolean; ownerOnly?: boolean; publicExpiresAt?: string; }) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    const store = get();

    const permissionLevel: PermissionLevel = options?.permissionLevel || 'basic';
    const ownerOnly = !!options?.ownerOnly;
    const isPublic = !!options?.isPublic && !ownerOnly;
    const publicExpiresAtIso = isPublic && options?.publicExpiresAt
      ? new Date(options.publicExpiresAt + 'T23:59:59').toISOString()
      : null;

    // 🔍 增量上传：查询当前分类下已有的所有文件名（分页获取，突破 1000 条限制）
    let existingFileNames = new Set<string>();
    if (categoryId) {
      try {
        const PAGE_SIZE = 1000;
        let offset = 0;
        let hasMore = true;

        while (hasMore) {
          const { data: existingFiles, error } = await supabase
            .from('file_categories')
            .select('file:files!inner(name, deleted_at)')
            .eq('category_id', categoryId)
            .is('file.deleted_at', null)
            .range(offset, offset + PAGE_SIZE - 1);
          
          if (error) {
            console.warn('查询已有文件失败:', error);
            break;
          }

          if (!existingFiles || existingFiles.length === 0) {
            hasMore = false;
          } else {
            for (const fc of existingFiles) {
              const fileName = (fc.file as any)?.name;
              if (fileName) {
                existingFileNames.add(fileName);
              }
            }
            offset += existingFiles.length;
            if (existingFiles.length < PAGE_SIZE) {
              hasMore = false;
            }
          }
        }
        console.log(`📁 当前分类已有 ${existingFileNames.size} 个文件（分页获取完成）`);
      } catch (err) {
        console.warn('查询已有文件失败，将上传全部文件:', err);
      }
    }

    // 过滤掉已存在的文件
    const newFiles = files.filter(f => !existingFileNames.has(f.name));
    const skippedCount = files.length - newFiles.length;
    
    if (skippedCount > 0) {
      console.log(`⏭️ 跳过 ${skippedCount} 个已存在的文件，将上传 ${newFiles.length} 个新文件`);
    }
    
    // 如果没有新文件需要上传
    if (newFiles.length === 0) {
      console.log('✨ 所有文件都已存在，无需上传');
      onProgress?.(files.length, files.length);
      return;
    }

    // 🚀 优化：根据文件数量动态调整并发数
    // Cloudflare R2: 支持约 5000 RPS/bucket，无硬性限制
    // Supabase: 写操作每个请求很快完成，可以处理较高并发
    // 浏览器 HTTP 并发限制约 6-8 个，但我们用 Promise 队列突破
    // 
    // 小批量(<=50)：30 并发
    // 中批量(51-500)：40 并发
    // 大批量(>500)：50 并发，跳过缩略图
    const total = newFiles.length;
    const originalTotal = files.length; // 用于进度显示
    const CONCURRENCY = total <= 50 ? 30 : total <= 500 ? 40 : 50;
    const SKIP_THUMBNAIL = total > 50; // 大批量时跳过即时缩略图生成
    
    let processed = skippedCount; // 从跳过的数量开始计数
    let failed = 0;

    const queue = [...newFiles]; // 使用过滤后的新文件列表
    const workers: Promise<void>[] = [];

    const runWorker = async () => {
      while (queue.length > 0) {
        const file = queue.shift()!;
        try {
          const storageKey = `${user.id}/${Date.now()}-${file.name}`;
          const fileType = getFileTypeFromExtension(file.name.split('.').pop() || '');

          // Create file record first
          const { data: fileRecord, error: insertError } = await supabase
            .from('files')
            .insert({
              name: file.name,
              type: fileType,
              size: file.size,
              storage_key: storageKey,
              created_by: user.id,
              updated_by: user.id,
              min_permission_level: permissionLevel,
              is_public: isPublic,
              owner_only: ownerOnly,
              public_access_token: isPublic ? generatePublicAccessToken() : null,
              public_expires_at: publicExpiresAtIso
            })
            .select()
            .single();

          if (insertError || !fileRecord) {
            throw insertError || new Error('Failed to create file record');
          }

          // Upload to R2
          await uploadToR2(file, storageKey);

          // Add tags to file (批量)
          if (tags.length > 0) {
            const { error: tagError } = await supabase
              .from('file_tags')
              .insert(
                tags.map(tag => ({
                  file_id: fileRecord.id,
                  tag_id: tag.id
                }))
              );

            if (tagError) console.warn('Error adding tags:', tagError);
          }

          // Add to category if specified
          if (categoryId) {
            const { error: categoryError } = await supabase
              .from('file_categories')
              .insert({
                file_id: fileRecord.id,
                category_id: categoryId
              });

            if (categoryError) {
              console.warn('Error adding file to category:', categoryError);
            }
          }

          // 🚀 优化：大批量上传时跳过即时缩略图生成
          // 后续可以通过 "processMissingThumbnails" 函数批量生成
          if (!SKIP_THUMBNAIL && ['image', 'video', 'pdf'].includes(fileType)) {
            try {
              let thumbnailBlob: Blob | null = null;

              if (fileType === 'image') {
                thumbnailBlob = await generateImageThumbnail(file);
              } else if (fileType === 'pdf') {
                thumbnailBlob = await generatePdfThumbnailFromFile(file);
              } else if (fileType === 'video') {
                thumbnailBlob = await generateVideoThumbnail(file);
              }

              if (thumbnailBlob) {
                const thumbnailKey = `thumbnails/${storageKey}`;
                await uploadToR2(new File([thumbnailBlob], `thumbnail-${file.name}.jpg`, { type: 'image/jpeg' }), thumbnailKey);

                await supabase
                  .from('files')
                  .update({
                    thumbnail_key: thumbnailKey,
                    thumbnail_generated_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                  })
                  .eq('id', fileRecord.id);
              }
            } catch (error) {
              console.warn('Error generating thumbnail:', error);
            }
          }

          processed += 1;
          onProgress?.(processed, originalTotal);
          
        } catch (error) {
          console.error('Error uploading file:', file.name, error);
          failed += 1;
          processed += 1;
          onProgress?.(processed, originalTotal);
          // 继续处理其他文件，不中断
        }
      }
    };

    // 启动 worker
    for (let i = 0; i < Math.min(CONCURRENCY, files.length); i++) {
      workers.push(runWorker());
    }

    await Promise.all(workers);
    
    // 上传完成后刷新文件列表
    await store.fetchFiles();
    
    // 如果跳过了缩略图，提示用户
    if (SKIP_THUMBNAIL && total > 50) {
      console.log(`🎉 批量上传完成！成功 ${processed - failed}/${total}，缩略图将在后台生成`);
    }
  },

  clearFiles: () => set({
    files: [],
    filteredFiles: [],
    selectedFile: null,
    favoriteFiles: [],
    selectedTags: [],
    searchQuery: '',
    lastFetch: 0,
    initialized: false,
    searchType: 'basic',
    isLoading: false,
    error: null,
    loading: false,
    thumbnailUpdatedFiles: new Set<string>()
  }),

  // 为现有文件生成缩略图
  processMissingThumbnails: async () => {
    const { files } = get();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // 筛选出没有缩略图的文件
    const filesWithoutThumbnails = files.filter(file =>
      !file.thumbnail_key &&
      ['image', 'video', 'pdf'].includes(file.type) &&
      file.storage_key
    );

    if (filesWithoutThumbnails.length === 0) {
      console.log('No files require thumbnail generation');
      return { processed: 0 };
    }

    console.log(`Processing ${filesWithoutThumbnails.length} files for thumbnail generation`);

    let processed = 0;
    let failed = 0;

    // 每个文件的进度通知
    const notify = (current: number, total: number, status: 'success' | 'error' = 'success') => {
      // 这里可以添加通知用户的逻辑，例如使用 toast 消息
      console.log(`Processed ${current}/${total} files (${status})`);
    };

    // 按批次处理文件（每次处理5个文件，避免浏览器负载过大）
    const batchSize = 3; // 减少批次大小，降低浏览器负担
    const batches = Math.ceil(filesWithoutThumbnails.length / batchSize);

    for (let i = 0; i < batches; i++) {
      const batch = filesWithoutThumbnails.slice(i * batchSize, (i + 1) * batchSize);

      // 串行处理当前批次中的文件，避免并发请求导致的服务器拒绝
      for (const file of batch) {
        try {
          console.log(`Processing file: ${file.name} (${file.type})`);

          if (!file.storage_key) {
            console.warn(`File ${file.name} has no storage key, skipping`);
            failed++;
            continue;
          }

          // 对于图片文件，直接生成占位符缩略图而不是获取原始文件
          // 这样可以避免因为无法获取原始文件导致的错误
          let thumbnailBlob: Blob | null = null;

          if (file.type === 'image') {
            try {
              // 先尝试获取文件内容
              console.log(`Fetching image: ${file.name}`);
              const url = await getPublicUrl(file.storage_key);

              // 添加超时和错误处理
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), 10000); // 10秒超时

              try {
                const response = await fetch(url, {
                  signal: controller.signal,
                  headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                  }
                });
                clearTimeout(timeoutId);

                if (!response.ok) {
                  throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
                }

                const contentType = response.headers.get('content-type') || '';
                const fileBlob = await response.blob();

                // 如果文件过大，直接生成占位符缩略图
                if (fileBlob.size > 10 * 1024 * 1024) { // 文件大于10MB
                  console.log(`File ${file.name} is too large (${fileBlob.size} bytes), using placeholder`);
                  thumbnailBlob = await generatePlaceholderThumbnail('image');
                } else {
                  const fileObj = new File([fileBlob], file.name, { type: contentType });
                  thumbnailBlob = await generateImageThumbnail(fileObj);
                }
              } catch (fetchError) {
                console.error(`Error fetching image ${file.name}:`, fetchError);
                // 如果获取失败，则使用占位图
                thumbnailBlob = await generatePlaceholderThumbnail('image');
              }
            } catch (error) {
              console.error(`Error processing image ${file.name}:`, error);
              thumbnailBlob = await generatePlaceholderThumbnail('image');
            }
          } else if (file.type === 'pdf') {
            try {
              const pdfUrl = await getPublicUrl(file.storage_key);
              thumbnailBlob = await generatePdfThumbnailFromUrl(pdfUrl);
            } catch (pdfErr) {
              console.warn(`PDF 首页截图生成失败: ${file.name}`, pdfErr);
              thumbnailBlob = await generatePlaceholderThumbnail('pdf');
            }
          } else if (file.type === 'video') {
            thumbnailBlob = await generatePlaceholderThumbnail('video');
          }

          if (!thumbnailBlob) {
            throw new Error('Failed to generate thumbnail');
          }

          // 上传缩略图
          console.log(`Uploading thumbnail for: ${file.name}`);
          const basePath = file.storage_key.replace(/\.[^.]+$/, '');
          const thumbnailKey = `thumbnails/${basePath}.jpg`;
          await uploadToR2(new File([thumbnailBlob], `thumbnail-${file.name}.jpg`, { type: 'image/jpeg' }), thumbnailKey);

          // 更新数据库记录
          console.log(`Updating database record for: ${file.name}`);
          const { error: updateError } = await supabase
            .from('files')
            .update({
              thumbnail_key: thumbnailKey,
              thumbnail_generated_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .eq('id', file.id);

          if (updateError) {
            throw updateError;
          }

          processed++;
          notify(processed + failed, filesWithoutThumbnails.length);
          console.log(`Successfully processed: ${file.name}`);
        } catch (error) {
          console.error(`Error processing file ${file.name}:`, error);
          failed++;
          notify(processed + failed, filesWithoutThumbnails.length, 'error');
        }

        // 每个文件处理后短暂暂停，避免服务器过载
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // 每批次处理完后暂停，给浏览器喘息的机会
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    // 处理完成后，刷新文件列表
    await get().fetchFiles();

    return { processed, failed, total: filesWithoutThumbnails.length };
  },

  // 批量删除文件（含存储与数据库），带进度回调
  deleteFiles: async (ids: string[], onProgress?: (processed: number, total: number) => void) => {
    const total = ids.length;
    let deleted = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        const file = get().files.find(f => f.id === id);
        if (file?.storage_key) {
          try { await deleteFromR2(file.storage_key); } catch { }
        }
        const { error } = await supabase.from('files').delete().eq('id', id);
        if (error) throw error;
        deleted += 1;
      } catch (e) {
        failed += 1;
      } finally {
        onProgress?.(deleted + failed, total);
      }
    }
    await get().fetchFiles();
    return { deleted, failed, total };
  },

  // 批量为指定文件生成缩略图（前端占位/简化版）
  generateThumbnailsFor: async (ids: string[], onProgress?: (processed: number, total: number) => void) => {
    const files = get().files.filter(f => ids.includes(f.id) && ['image', 'video', 'pdf'].includes(f.type));
    const total = files.length;
    let processed = 0;
    let failed = 0;
    for (const file of files) {
      try {
        if (!file.storage_key) { failed++; onProgress?.(processed + failed, total); continue; }
        let thumbnailBlob: Blob | null = null;

        if (file.type === 'pdf') {
          const pdfUrl = await getPublicUrl(file.storage_key);
          thumbnailBlob = await generatePdfThumbnailFromUrl(pdfUrl);
        } else if (file.type === 'image') {
          const imgUrl = await getPublicUrl(file.storage_key);
          const response = await fetch(imgUrl);
          const blob = await response.blob();
          thumbnailBlob = await generateImageThumbnail(new File([blob], file.name, { type: blob.type }));
        } else {
          // video — fallback to edge function if available
          try {
            const { error } = await supabase.functions.invoke('generate-thumbnail', {
              body: { fileId: file.id, storageKey: file.storage_key },
            });
            if (error) throw error;
            processed++;
            onProgress?.(processed + failed, total);
            continue;
          } catch {
            failed++;
            onProgress?.(processed + failed, total);
            continue;
          }
        }

        if (thumbnailBlob) {
          // 将原始扩展名替换为 .jpg（否则 CDN 按 .pdf 返回错误的 content-type）
          const basePath = file.storage_key.replace(/\.[^.]+$/, '');
          const thumbnailKey = `thumbnails/${basePath}.jpg`;
          await uploadToR2(
            new File([thumbnailBlob], `thumbnail-${file.name}.jpg`, { type: 'image/jpeg' }),
            thumbnailKey
          );
          await supabase.from('files').update({ thumbnail_key: thumbnailKey }).eq('id', file.id);
        }
        processed++;
      } catch (err) {
        console.warn(`缩略图生成失败: ${file.name}`, err);
        failed++;
      } finally {
        onProgress?.(processed + failed, total);
      }
    }
    await get().fetchFiles();
    return { processed, failed, total };
  },

  // 移动文件到指定分类
  moveFilesToCategory: async (ids: string[], targetCategoryId: string, onProgress?: (processed: number, total: number) => void) => {
    const total = ids.length;
    let moved = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        // 先删除旧的 file_categories 关系，再插入新的
        await supabase.from('file_categories').delete().eq('file_id', id);
        const { error } = await supabase.from('file_categories').insert({ file_id: id, category_id: targetCategoryId });
        if (error) throw error;
        moved += 1;
      } catch (e) {
        failed += 1;
      } finally {
        onProgress?.(moved + failed, total);
      }
    }
    await get().fetchFiles();
    try { await useCategoryStore.getState().fetchCategories(); } catch { }
    return { moved, failed, total };
  },

  // 专门用于更新缩略图的方法
  updateFileThumbnail: async (id, thumbnailKey) => {
    try {
      console.log('更新文件缩略图，ID:', id, '缩略图Key:', thumbnailKey);

      // 确保thumbnailKey是有效的
      if (!thumbnailKey) {
        throw new Error('缩略图Key不能为空');
      }

      // 确保thumbnailKey有正确的前缀
      const validKey = thumbnailKey.startsWith('thumbnails/')
        ? thumbnailKey
        : `thumbnails/${thumbnailKey}`;

      // 更新数据库
      const { error } = await supabase
        .from('files')
        .update({
          thumbnail_key: validKey,
          thumbnail_generated_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      // 先创建新的Set并添加ID，确保引用发生变化
      const updatedThumbnailFiles = new Set(get().thumbnailUpdatedFiles);
      updatedThumbnailFiles.add(id);

      console.log('缩略图更新标记已添加，文件ID:', id);
      console.log('更新前的Set:', [...get().thumbnailUpdatedFiles]);
      console.log('更新后的Set:', [...updatedThumbnailFiles]);

      // 先更新标记集合，确保UI能立即感知变化
      set({ thumbnailUpdatedFiles: updatedThumbnailFiles });

      // 再更新文件数组
      set(state => {
        // 找到文件
        const fileIndex = state.files.findIndex(file => file.id === id);
        if (fileIndex === -1) return state;

        // 创建新的文件数组
        const newFiles = [...state.files];
        // 更新指定文件
        newFiles[fileIndex] = {
          ...newFiles[fileIndex],
          thumbnail_key: validKey,
          thumbnail_generated_at: new Date().toISOString()
        };

        // 同步更新 categoryFiles 缓存（确保返回分类页时缩略图已更新）
        const newCategoryFiles = new Map(state.categoryFiles);
        for (const [catId, catFiles] of newCategoryFiles.entries()) {
          const catFileIndex = catFiles.findIndex(f => f.id === id);
          if (catFileIndex !== -1) {
            const updatedCatFiles = [...catFiles];
            updatedCatFiles[catFileIndex] = {
              ...updatedCatFiles[catFileIndex],
              thumbnail_key: validKey,
              thumbnail_generated_at: new Date().toISOString()
            };
            newCategoryFiles.set(catId, updatedCatFiles);
          }
        }

        // 返回新状态 - 不更新thumbnailUpdatedFiles，因为已经在前面更新过了
        return { files: newFiles, categoryFiles: newCategoryFiles };
      });

      console.log('缩略图已更新，文件ID:', id);

      // 刷新所有文件以确保同步 - 延迟一点执行
      setTimeout(async () => {
        await get().fetchFiles();
        console.log('文件列表已刷新');
      }, 500);

    } catch (error) {
      console.error('更新缩略图失败:', (error instanceof Error ? error.message : String(error)));
      throw error;
    }
  },

  // 清除缩略图更新标记
  clearThumbnailUpdated: (id) => {
    set(state => {
      const newUpdatedFiles = new Set(state.thumbnailUpdatedFiles);
      newUpdatedFiles.delete(id);
      console.log(`已清除文件ID:${id}的缩略图更新标记`);
      return { thumbnailUpdatedFiles: newUpdatedFiles };
    });
  },

  // 🆕 分页加载文件夹内的文件（无限滚动支持）
  fetchCategoryFiles: async (categoryId: string, options?: { reset?: boolean; pageSize?: number }) => {
    // 智能分页策略：
    // - 小文件夹（<=100）：一次性加载全部
    // - 大文件夹首次：快速加载50个
    // - 后续分批：每次200个
    const reset = options?.reset ?? false;
    const state = get();
    
    const cachedFiles = state.categoryFiles.get(categoryId);
    const hasMoreCached = state.categoryFilesHasMore.get(categoryId);
    
    // 如果是重置，跳过缓存检查
    // 如果已有缓存且已加载完毕（hasMore=false），直接返回缓存
    if (!reset && cachedFiles && cachedFiles.length > 0 && hasMoreCached === false) {
      console.log(`[fetchCategoryFiles] 使用缓存（已完成）: ${categoryId}, ${cachedFiles.length} 个文件`);
      return { 
        files: cachedFiles, 
        hasMore: false,
        totalCount: state.categoryFilesTotalCount.get(categoryId) ?? cachedFiles.length
      };
    }
    
    // 如果正在加载中，直接返回当前缓存（防止重复请求）
    if (state.categoryFilesLoading.get(categoryId)) {
      console.log(`[fetchCategoryFiles] 正在加载中，跳过: ${categoryId}`);
      const existingFiles = cachedFiles || [];
      return { 
        files: existingFiles, 
        hasMore: hasMoreCached ?? true,
        totalCount: state.categoryFilesTotalCount.get(categoryId) ?? 0
      };
    }

    // 设置加载状态
    const newLoadingMap = new Map(state.categoryFilesLoading);
    newLoadingMap.set(categoryId, true);
    set({ categoryFilesLoading: newLoadingMap });

    try {
      // 当前已加载的文件
      const existingFiles = reset ? [] : (cachedFiles || []);
      const offset = existingFiles.length;

      // 先获取文件总数
      const { count: totalCount } = await supabase
        .from('file_categories')
        .select('*', { count: 'exact', head: true })
        .eq('category_id', categoryId);

      const actualTotal = totalCount || 0;

      // 获取文件总大小（只在首次加载时查询）
      let totalSize = state.categoryFilesTotalSize.get(categoryId);
      if (totalSize === undefined && offset === 0) {
        const { data: sizeData } = await supabase.rpc('get_category_total_size', { p_category_id: categoryId });
        totalSize = sizeData || 0;
        
        // 如果 RPC 不存在，则在加载完所有文件后计算
        if (totalSize === 0 && actualTotal > 0) {
          // 暂时设置为 -1 表示需要后续计算
          totalSize = -1;
        }
        
        const newSizeMap = new Map(state.categoryFilesTotalSize);
        newSizeMap.set(categoryId, totalSize);
        set({ categoryFilesTotalSize: newSizeMap });
      }
      
      // 🆕 智能决策：
      // - 小文件夹（<=100）：一次性加载全部
      // - 首次加载：快速显示50个
      // - 后续加载：每批200个
      const isSmallFolder = actualTotal <= 100;
      const isFirstLoad = offset === 0;
      const FIRST_LOAD_SIZE = 50;  // 首次快速加载
      const BATCH_SIZE = 200;       // 后续批量加载
      
      let pageSize: number;
      if (isSmallFolder) {
        pageSize = Math.max(actualTotal, 1); // 小文件夹全部加载，最少1防止0
      } else if (isFirstLoad) {
        pageSize = FIRST_LOAD_SIZE; // 首次快速加载
      } else {
        pageSize = BATCH_SIZE; // 后续批量加载
      }
      
      console.log(`[fetchCategoryFiles] 加载分类 ${categoryId}: total=${actualTotal}, offset=${offset}, pageSize=${pageSize}, isSmall=${isSmallFolder}, isFirst=${isFirstLoad}`);

      // 🆕 如果文件夹为空或已加载完毕，立即返回并正确设置状态
      if (actualTotal === 0 || offset >= actualTotal) {
        console.log(`[fetchCategoryFiles] 分类 ${categoryId} 已完成或为空`);
        
        // 更新所有状态
        const newFilesMap = new Map(state.categoryFiles);
        newFilesMap.set(categoryId, existingFiles);
        
        const newHasMoreMap = new Map(state.categoryFilesHasMore);
        newHasMoreMap.set(categoryId, false); // 关键：设为 false 防止循环
        
        const newTotalCountMap = new Map(state.categoryFilesTotalCount);
        newTotalCountMap.set(categoryId, actualTotal);
        
        const newLoadingMapFinal = new Map(state.categoryFilesLoading);
        newLoadingMapFinal.set(categoryId, false);
        
        set({ 
          categoryFiles: newFilesMap,
          categoryFilesHasMore: newHasMoreMap,
          categoryFilesTotalCount: newTotalCountMap,
          categoryFilesLoading: newLoadingMapFinal
        });
        
        return { files: existingFiles, hasMore: false, totalCount: actualTotal };
      }

      // 分页查询文件
      const { data: fileCategories, error } = await supabase
        .from('file_categories')
        .select(`
          file:files(
            *,
            tags:file_tags(tag:tags(*))
          )
        `)
        .eq('category_id', categoryId)
        .order('created_at', { foreignTable: 'files', ascending: false })
        .range(offset, offset + pageSize - 1);

      if (error) throw error;

      // 转换数据格式
      const newFiles = (fileCategories || [])
        .map((fc: any) => fc.file)
        .filter(Boolean)
        .map((file: any) => ({
          ...file,
          tags: file.tags?.map((t: any) => t.tag) || [],
          createdAt: file.created_at,
          updatedAt: file.updated_at,
          createdBy: file.created_by,
          updatedBy: file.updated_by
        })) as KnowledgeFile[];

      const allFiles = [...existingFiles, ...newFiles];
      const hasMore = allFiles.length < actualTotal;

      console.log(`[fetchCategoryFiles] 已加载 ${allFiles.length}/${actualTotal} 个文件, hasMore=${hasMore}`);

      // 更新缓存（永久保存）
      const newFilesMap = new Map(state.categoryFiles);
      newFilesMap.set(categoryId, allFiles);

      const newHasMoreMap = new Map(state.categoryFilesHasMore);
      newHasMoreMap.set(categoryId, hasMore);

      const newTotalCountMap = new Map(state.categoryFilesTotalCount);
      newTotalCountMap.set(categoryId, actualTotal);

      const newLoadingMapFinal = new Map(state.categoryFilesLoading);
      newLoadingMapFinal.set(categoryId, false);

      set({ 
        categoryFiles: newFilesMap,
        categoryFilesHasMore: newHasMoreMap,
        categoryFilesTotalCount: newTotalCountMap,
        categoryFilesLoading: newLoadingMapFinal
      });

      return { files: allFiles, hasMore, totalCount: actualTotal };
    } catch (error) {
      console.error('[fetchCategoryFiles] 加载失败:', error);
      
      // 清除加载状态
      const newLoadingMapFinal = new Map(state.categoryFilesLoading);
      newLoadingMapFinal.set(categoryId, false);
      set({ categoryFilesLoading: newLoadingMapFinal });
      
      throw error;
    }
  },

  // 清除分类文件缓存（刷新或切换分类时使用）
  clearCategoryFilesCache: (categoryId?: string) => {
    if (categoryId) {
      // 清除指定分类的缓存
      set(state => {
        const newFilesMap = new Map(state.categoryFiles);
        const newLoadingMap = new Map(state.categoryFilesLoading);
        const newHasMoreMap = new Map(state.categoryFilesHasMore);
        const newTotalCountMap = new Map(state.categoryFilesTotalCount);
        
        newFilesMap.delete(categoryId);
        newLoadingMap.delete(categoryId);
        newHasMoreMap.delete(categoryId);
        newTotalCountMap.delete(categoryId);
        
        return {
          categoryFiles: newFilesMap,
          categoryFilesLoading: newLoadingMap,
          categoryFilesHasMore: newHasMoreMap,
          categoryFilesTotalCount: newTotalCountMap
        };
      });
    } else {
      // 清除所有缓存
      set({
        categoryFiles: new Map(),
        categoryFilesLoading: new Map(),
        categoryFilesHasMore: new Map(),
        categoryFilesTotalCount: new Map()
      });
    }
  },

  // 🆕 服务端搜索：在特定分类下搜索文件（支持递归子分类）
  searchInCategory: async (query: string, options?: { categoryId?: string; limit?: number }) => {
    const limit = options?.limit ?? 50;
    const categoryId = options?.categoryId;
    
    if (!query || query.trim().length < 1) {
      return { files: [], totalCount: 0 };
    }
    
    const searchTerm = query.trim().toLowerCase();
    console.log(`[searchInCategory] 搜索: "${searchTerm}", 分类: ${categoryId || '全局'}, 限制: ${limit}`);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        console.log('No active session, skipping search');
        return { files: [], totalCount: 0 };
      }

      // 如果指定了分类，需要收集该分类及其所有子分类的ID
      let categoryIds: string[] = [];
      if (categoryId) {
        // 获取分类树以收集所有子分类ID
        const { categories } = useCategoryStore.getState();
        
        const collectCategoryIds = (cats: any[], targetId: string): string[] => {
          const ids: string[] = [];
          const findAndCollect = (node: any): boolean => {
            if (node.id === targetId) {
              // 找到目标分类，收集它及其所有子分类
              const collectAll = (n: any) => {
                ids.push(n.id);
                (n.children || []).forEach(collectAll);
              };
              collectAll(node);
              return true;
            }
            // 递归查找子节点
            for (const child of (node.children || [])) {
              if (findAndCollect(child)) return true;
            }
            return false;
          };
          
          for (const cat of cats) {
            if (findAndCollect(cat)) break;
          }
          return ids;
        };
        
        categoryIds = collectCategoryIds(categories, categoryId);
        console.log(`[searchInCategory] 分类ID列表 (含子分类): ${categoryIds.length} 个`);
        
        if (categoryIds.length === 0) {
          console.log(`[searchInCategory] 未找到分类 ${categoryId}`);
          return { files: [], totalCount: 0 };
        }
      }

      // 构建搜索查询
      if (categoryIds.length > 0) {
        // 在特定分类下搜索：通过 file_categories 关联表
        // 使用 ilike 进行不区分大小写的模糊匹配
        const { data: fileCategories, error, count } = await supabase
          .from('file_categories')
          .select(`
            file:files!inner(
              *,
              tags:file_tags(tag:tags(*))
            )
          `, { count: 'exact' })
          .in('category_id', categoryIds)
          .ilike('file.name', `%${searchTerm}%`)
          .is('file.deleted_at', null)
          .order('created_at', { foreignTable: 'files', ascending: false })
          .limit(limit);
        
        if (error) {
          console.error('[searchInCategory] 搜索失败:', error);
          throw error;
        }
        
        // 转换数据格式并去重（一个文件可能在多个子分类中）
        const seen = new Set<string>();
        const files = (fileCategories || [])
          .map((fc: any) => fc.file)
          .filter((file: any) => {
            if (!file || seen.has(file.id)) return false;
            seen.add(file.id);
            return true;
          })
          .map((file: any) => ({
            ...file,
            tags: file.tags?.map((t: any) => t.tag) || [],
            createdAt: file.created_at,
            updatedAt: file.updated_at,
            createdBy: file.created_by,
            updatedBy: file.updated_by
          })) as KnowledgeFile[];
        
        console.log(`[searchInCategory] 找到 ${files.length} 个文件 (总计 ${count || 0})`);
        return { files, totalCount: count || files.length };
        
      } else {
        // 全局搜索：直接查询 files 表
        const { data: files, error, count } = await supabase
          .from('files')
          .select(`
            *,
            tags:file_tags(tag:tags(*))
          `, { count: 'exact' })
          .ilike('name', `%${searchTerm}%`)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })
          .limit(limit);
        
        if (error) {
          console.error('[searchInCategory] 全局搜索失败:', error);
          throw error;
        }
        
        // 转换数据格式
        const transformedFiles = (files || []).map((file: any) => ({
          ...file,
          tags: file.tags?.map((t: any) => t.tag) || [],
          createdAt: file.created_at,
          updatedAt: file.updated_at,
          createdBy: file.created_by,
          updatedBy: file.updated_by
        })) as KnowledgeFile[];
        
        console.log(`[searchInCategory] 全局搜索找到 ${transformedFiles.length} 个文件 (总计 ${count || 0})`);
        return { files: transformedFiles, totalCount: count || transformedFiles.length };
      }
    } catch (error) {
      console.error('[searchInCategory] 搜索出错:', error);
      return { files: [], totalCount: 0 };
    }
  },

  uploadFromUrl: async (fileUrl: string, fileName: string, tags: Tag[], categoryId?: string, options?: { permissionLevel?: PermissionLevel; isPublic?: boolean; ownerOnly?: boolean; publicExpiresAt?: string; }) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      console.log('Starting file upload from URL:', fileUrl);

      if (!fileName) {
        throw new Error('File name is required');
      }

      // 获取文件扩展名和类型
      const originalExtension = fileUrl.split('/').pop()?.split('.').pop() || '';
      const fileExtension = fileName.includes('.') ? fileName.split('.').pop() || originalExtension : originalExtension;

      // 确保文件名有正确的扩展名
      const finalFileName = fileName.includes('.') ? fileName : `${fileName}.${fileExtension}`;
      const fileType = getFileTypeFromExtension(fileExtension);

      // 从URL下载文件
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch file from URL: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const file = new File([blob], finalFileName, { type: blob.type });

      // 生成存储路径
      const storageKey = `${user.id}/${Date.now()}-${finalFileName}`;

      // 创建文件记录
      const permissionLevel: PermissionLevel = options?.permissionLevel || 'basic';
      const ownerOnly = !!options?.ownerOnly;
      const isPublic = !!options?.isPublic && !ownerOnly;
      const publicExpiresAtIso = isPublic && options?.publicExpiresAt
        ? new Date(options.publicExpiresAt + 'T23:59:59').toISOString()
        : null;

      const { data: fileRecord, error: insertError } = await supabase
        .from('files')
        .insert({
          name: finalFileName,
          type: fileType,
          size: file.size,
          storage_key: storageKey,
          created_by: user.id,
          updated_by: user.id,
          min_permission_level: permissionLevel,
          is_public: isPublic,
          owner_only: ownerOnly,
          public_access_token: isPublic ? generatePublicAccessToken() : null,
          public_expires_at: publicExpiresAtIso
        })
        .select()
        .single();

      if (insertError || !fileRecord) {
        throw insertError || new Error('Failed to create file record');
      }

      // 上传到R2
      await uploadToR2(file, storageKey);

      // 添加标签
      if (tags.length > 0) {
        const { error: tagError } = await supabase
          .from('file_tags')
          .insert(
            tags.map(tag => ({
              file_id: fileRecord.id,
              tag_id: tag.id
            }))
          );

        if (tagError) throw tagError;
      }

      // 添加到分类（如果指定）
      if (categoryId) {
        const { error: categoryError } = await supabase
          .from('file_categories')
          .insert({
            file_id: fileRecord.id,
            category_id: categoryId
          });

        if (categoryError) {
          console.error('Error adding file to category:', categoryError);
        }
      }



      // 更新文件列表
      get().fetchFiles();

      console.log('URL file upload completed successfully');
      return fileRecord;

    } catch (error: any) {
      console.error('URL file upload failed:', {
        error,
        errorMessage: error.message,
        errorStack: error.stack,
      });
      throw error;
    }
  },
}));

// Helper functions for thumbnail generation
const generateImageThumbnail = async (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Create a thumbnail that maintains aspect ratio
      const canvas = document.createElement('canvas');
      const MAX_WIDTH = 400;
      const MAX_HEIGHT = 400;
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width *= MAX_HEIGHT / height;
          height = MAX_HEIGHT;
        }
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Canvas toBlob failed'));
          }
        },
        'image/jpeg',
        0.8
      );
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = URL.createObjectURL(file);
  });
};

const generateVideoThumbnail = async (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    try {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.playsInline = true;
      video.muted = true;

      video.onloadedmetadata = () => {
        video.currentTime = 1; // Seek to 1 second (to avoid black frames)
      };

      video.onseeked = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = 400;
          canvas.height = 400 * (video.videoHeight / video.videoWidth);

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(video.src); // Clean up

          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Canvas toBlob failed'));
              }
            },
            'image/jpeg',
            0.8
          );
        } catch (err) {
          reject(err);
        }
      };

      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        reject(new Error('Failed to load video'));
      };

      video.src = URL.createObjectURL(file);
    } catch (err) {
      reject(err);
    }
  });
};

const generatePlaceholderThumbnail = async (type: 'pdf' | 'video' | 'image' | 'default'): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 400;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Failed to get canvas context'));
      return;
    }

    // Set background
    if (type === 'pdf') {
      ctx.fillStyle = '#F2F2F2';
    } else if (type === 'video') {
      ctx.fillStyle = '#000000';
    } else if (type === 'image') {
      ctx.fillStyle = '#E0F2F1'; // 浅青色背景用于图片
    } else {
      ctx.fillStyle = '#607D8B';
    }

    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw icon
    ctx.fillStyle = type === 'pdf' ? '#F40F02' : '#FFFFFF';
    ctx.font = 'bold 60px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    if (type === 'pdf') {
      ctx.fillText('PDF', canvas.width / 2, canvas.height / 2);
    } else if (type === 'video') {
      // Draw play button triangle
      ctx.beginPath();
      ctx.moveTo(canvas.width / 2 - 30, canvas.height / 2 - 50);
      ctx.lineTo(canvas.width / 2 - 30, canvas.height / 2 + 50);
      ctx.lineTo(canvas.width / 2 + 50, canvas.height / 2);
      ctx.closePath();
      ctx.fill();
    } else if (type === 'image') {
      // 绘制图片图标
      ctx.fillStyle = '#26A69A'; // 青色
      ctx.fillText('IMG', canvas.width / 2, canvas.height / 2);
    } else {
      ctx.fillText('FILE', canvas.width / 2, canvas.height / 2);
    }

    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas toBlob failed'));
        }
      },
      'image/jpeg',
      0.8
    );
  });
};